import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { CalendarPlus } from "lucide-react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Loading, Empty, Uploader } from "@/components/atoms";
import Gate from "@/components/Gate";

const EMPTY = { tattoo_idea: "", size: "", placement: "", style: "", preferred_artist: "", preferred_dates: "", budget: "", reference_url: "" };
const STATUS_COLORS = { pending: "#554E40", contacted: "#C7A656", scheduled: "#8B1E28", declined: "#121212" };

export default function Bookings() {
  const location = useLocation();
  const prefill = location.state?.prefill;
  const [form, setForm] = useState({ ...EMPTY, ...(prefill || {}) });
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(false);
  const [artists, setArtists] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/bookings/me"); setMine(data); } catch (e) { setMine([]); }
  };
  useEffect(() => {
    load();
    api.get("/artists").then(({ data }) => setArtists(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/bookings", form);
      toast.success("Booking request sent");
      setForm({ ...EMPTY });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Gate>
      <div className="p-5" data-testid="bookings-page">
        <span className="eyebrow">Get Inked</span>
        <h1 className="font-display font-black text-3xl uppercase leading-tight mb-4">Book A Session</h1>

        {prefill?.reference_url && (
          <div className="atc-card p-3 mb-4 flex items-center gap-3 bg-[#F4E9C9]" data-testid="booking-prefill-banner">
            <img src={fileUrl(prefill.reference_url)} alt="flash" className="w-14 h-14 object-cover border-2 border-black rounded" />
            <p className="text-sm font-body">You're booking a flash design — details are pre-filled below.</p>
          </div>
        )}

        <form onSubmit={submit} className="flex flex-col gap-3 mb-8">
          <textarea className="atc-input" rows={2} placeholder="Tattoo idea" value={form.tattoo_idea} onChange={set("tattoo_idea")} required data-testid="booking-idea" />
          <div className="grid grid-cols-2 gap-3">
            <input className="atc-input" placeholder="Size (e.g. 4in)" value={form.size} onChange={set("size")} required data-testid="booking-size" />
            <input className="atc-input" placeholder="Placement" value={form.placement} onChange={set("placement")} required data-testid="booking-placement" />
          </div>
          <input className="atc-input" placeholder="Style (e.g. Traditional)" value={form.style} onChange={set("style")} required data-testid="booking-style" />
          <select className="atc-input" value={form.preferred_artist} onChange={set("preferred_artist")} data-testid="booking-artist">
            <option value="">Preferred artist (optional)</option>
            {artists.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
          </select>
          <input className="atc-input" placeholder="Preferred dates" value={form.preferred_dates} onChange={set("preferred_dates")} data-testid="booking-dates" />
          <input className="atc-input" placeholder="Budget (e.g. $300)" value={form.budget} onChange={set("budget")} data-testid="booking-budget" />
          {form.reference_url && <img src={fileUrl(form.reference_url)} alt="reference" className="w-full h-40 object-cover border-2 border-black rounded" />}
          <Uploader onUploaded={(url) => setForm({ ...form, reference_url: url })} label="Upload reference photo" testid="booking-ref-upload" />
          <button type="submit" className="atc-btn btn-primary" disabled={busy} data-testid="booking-submit-btn">
            <CalendarPlus size={18} /> Send Request
          </button>
        </form>

        <h2 className="font-display font-bold text-xl uppercase mb-3">My Requests</h2>
        {mine === null ? <Loading /> : mine.length === 0 ? <Empty title="No requests yet" /> : (
          <div className="flex flex-col gap-3">
            {mine.map((b) => (
              <div key={b.id} className="atc-card p-4" data-testid={`my-booking-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-display font-bold leading-tight">{b.tattoo_idea}</h3>
                    <p className="font-mono2 text-xs text-[#554E40] mt-1">{b.style} · {b.size} · {b.placement}</p>
                  </div>
                  <span className="text-[#F4F0E6] border-2 border-black rounded px-2 py-1 font-mono2 text-[10px] uppercase shrink-0" style={{ background: STATUS_COLORS[b.status] }} data-testid={`booking-status-${b.id}`}>
                    {b.status}
                  </span>
                </div>
                {b.admin_note && <p className="text-sm mt-2 bg-[#F9F6F0] border border-black/30 rounded p-2"><b className="font-mono2 text-[10px] uppercase">Parlor note:</b> {b.admin_note}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </Gate>
  );
}
